import React            from 'react';
import AddressModel     from '@/models/services/fondations/AddressModel';
import EntityAddressForm from '@/pages/Entity/forms/EntityAddressForm';
import Modal            from '@/components/modal/Modal';
import { ModalData }    from '@/components/modal/Modal';
import { Text }         from '@chakra-ui/react';
import { toaster }      from '@/components/ui/toaster';
import { useTranslation } from 'react-i18next';

interface UseEntityAddressesBoxParams {
	entityUrn: string;
	onSuccess: () => void;
}

const useEntityAddressesBox = ({ entityUrn, onSuccess }: UseEntityAddressesBoxParams) => {
	const { t } = useTranslation();
	const [modalData, setModalData]     = React.useState<ModalData | undefined>();
	const [isModalOpen, setIsModalOpen] = React.useState(false);

	const closeModal = () => {
		setIsModalOpen(false);
		setModalData(undefined);
	};

	const openAddModal = () => {
		setModalData({
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.addresses.modals.add.title')}</Text>,
			bodyComponent:  (
				<EntityAddressForm
					entityUrn={entityUrn}
					onSuccess={async () => { onSuccess(); closeModal(); }}
					onClose={closeModal}
				/>
			),
			onClose: closeModal,
		});
		setIsModalOpen(true);
	};

	const openEditModal = (address: AddressModel) => {
		setModalData({
			model:          address,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.addresses.modals.edit.title')}</Text>,
			bodyComponent:  (
				<EntityAddressForm
					address={address}
					entityUrn={entityUrn}
					onSuccess={async () => { onSuccess(); closeModal(); }}
					onClose={closeModal}
				/>
			),
			onClose: closeModal,
		});
		setIsModalOpen(true);
	};

	const openDeleteModal = (address: AddressModel) => {
		setModalData({
			model:          address,
			isDangerous:    true,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.addresses.modals.delete.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.addresses.modals.delete.message')}</Text>,
			onClose:        closeModal,
			onConfirm:      async () => { await address.destroy(); },
			onSuccess:      () => {
				toaster.create({ title: t('pages.entity.addresses.modals.delete.messages.success'), type: 'success' });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.addresses.modals.delete.messages.error'), type: 'error' });
			},
		});
		setIsModalOpen(true);
	};

	const modalElement = (
		<Modal
			model={modalData?.model}
			isDangerous={modalData?.isDangerous}
			isOpen={isModalOpen}
			setIsOpen={setIsModalOpen}
			titleComponent={modalData?.titleComponent}
			bodyComponent={modalData?.bodyComponent}
			onConfirm={modalData?.onConfirm}
			onClose={modalData?.onClose}
			onError={modalData?.onError}
			onSuccess={modalData?.onSuccess}
		/>
	);

	return { modalElement, openAddModal, openEditModal, openDeleteModal };
};

export default useEntityAddressesBox;
